/**
 * Un élève de la séance, tel que le serveur le sert dans `live_session.users` (et que `getStudent` complète
 * dans `currentUser.students`), en morceaux prêts pour la pellicule du live du masteur : ses activités, son
 * drapeau de présence, son rattrapage.
 *
 * Ports littéraux de l'ancienne fiche du live (`tutor/students/NewStudentsListItem.js`, montée par
 * `TutorFocusLayoutM`), chaque fonction cite la sienne. Aucun accès au store : le view-model passe ce
 * qu'il a lu.
 */
import moment from "moment"

import { spentMinutes } from "../../parent/Activities/activityAdapter"
import { type ApiSchoolSubject, toUISubject } from "../../parent/shared/schoolSubjectAdapter"
import { type UITutorLiveActivity, type UITutorLiveFlag } from "../../../ui/types/tutorLive"
import { type ApiObjectiveStudent } from "./sessionObjectiveAdapter"

/** Une activité de l'élève, faite (`activities`) ou à faire (`todo_activities`). */
export type ApiLiveActivity = {
    id?: number | string | null
    title?: string | null
    status?: string | null
    student_id?: number | string | null
    school_subject_id?: number | string | null
    finished_at?: string | null
    created_at?: string | null
    started_at?: string | null
    locked_at?: string | null
    /** Le temps passé, en secondes. */
    duration?: number | null
    /** Le temps prévu, en minutes. */
    estimated_duration?: number | null
    media_count?: number | null
    activity_template_id?: number | string | null
    material?: { title?: string | null; school_subject_id?: number | string | null } | null
    student_chapter?: { chapter?: { name?: string | null } | null } | null
    /** Elle s'ouvre par un quiz de prérequis (`ActivityResource`). */
    has_prerequisite_quiz?: boolean | number | null
    /** Où en est l'élève de ce quiz : `pending` (ou absent) tant qu'il ne l'a ni réussi ni passé. */
    quiz_status?: string | null
}

/** L'élève, vu de la séance. */
export type ApiLiveStudent = ApiObjectiveStudent & {
    id?: number | string | null
    display_name?: string | null
    firstname?: string | null
    avatar?: string | null
    is_tutor?: boolean | null
    roles?: ({ name?: string | null } | string | null)[] | null
    is_payment_blocked?: boolean | null
    profile?: { class?: string | null } | null
    activities?: (ApiLiveActivity | null)[] | null
    todo_activities?: (ApiLiveActivity | null)[] | null
    conversations?:
        ({ id?: number | string | null; status?: string | null; trigger_context?: string | null } | null)[] | null
}

/** Un drapeau de présence posé sur la séance (`attendance_flags`). */
export type ApiAttendanceFlag = {
    user_id?: number | string | null
    date?: string | null
    type?: string | null
    late_minutes?: number | null
}

/** Une occurrence de la séance (`next_occurrences`). */
export type ApiLiveOccurrence = {
    id?: number | string | null
    date?: string | null
    finalized_at?: string | null
    added_users?: ({ id?: number | string | null } | null)[] | null
}

/** La salle de la séance (`live_room`). */
export type ApiLiveRoom = {
    type?: string | null
    version?: string | null
    version_ft?: string | null
    supports_no_show_recovery?: boolean | null
}

/** La séance, telle que `currentUser.live_sessions` la porte. */
export type ApiTutorLiveSession = {
    id?: number | string | null
    users?: (ApiLiveStudent | null)[] | null
    live_room?: ApiLiveRoom | null
    attendance_flags?: (ApiAttendanceFlag | null)[] | null
    next_occurrences?: (ApiLiveOccurrence | null)[] | null
    unread_private_messages_count?: Record<string, number | null> | null
}

/** Les salles de vacances (`NewStudentsListItem.js:153`) : le bandeau de matière, sans prochain contrôle. */
export function isHolidayRoom(room: ApiLiveRoom | null | undefined): boolean {
    return room?.type === "holiday_workbook" || room?.type === "holiday_course"
}

/** Le label d'une salle, lu comme l'ancien `hasFeature` (`data/utility/Utils.js`) : une sous-chaîne de la version. */
export function roomHasFeature(room: ApiLiveRoom | null | undefined, feature: string): boolean {
    const version = room?.version_ft ?? room?.version
    return typeof version === "string" && version.includes(feature)
}

/**
 * Une salle qui accepte le rattrapage : le drapeau du serveur, à défaut le type (port de
 * `data/utility/liveSessionType.js`, `supportsNoShowRecovery`, et de sa liste de repli).
 */
const NO_SHOW_RECOVERY_FALLBACK_TYPES = ["biweekly", "study_group", "holiday_workbook"]
export function supportsNoShowRecovery(room: ApiLiveRoom | null | undefined): boolean {
    return room?.supports_no_show_recovery ?? NO_SHOW_RECOVERY_FALLBACK_TYPES.includes(room?.type ?? "")
}

/** L'occurrence pas encore clôturée la plus proche (port de `findClosestNextOccurrence`, `LiveRoomCard.js`). */
export function closestNextOccurrence(session: ApiTutorLiveSession | null | undefined): ApiLiveOccurrence | null {
    const pending = (session?.next_occurrences ?? [])
        .filter((occurrence): occurrence is ApiLiveOccurrence => occurrence != null && !occurrence.finalized_at)
        .sort((a, b) => moment(a.date).diff(b.date))
    return pending[0] ?? null
}

/** Un élève est-il un tuteur (un co-tuteur dans la salle) ? `is_tutor` du serveur, ou son rôle. */
export function isTutorUser(user: ApiLiveStudent | null | undefined): boolean {
    if (user?.is_tutor === true) return true
    return (user?.roles ?? []).some((role) => (typeof role === "string" ? role : role?.name) === "tutor")
}

/**
 * Il rattrape une séance manquée : il est ajouté à la prochaine occurrence et la salle accepte le rattrapage
 * (`NewStudentsListItem.js:298-301`, l'ancien « * » et « *En rattrapage »).
 */
export function isRecovery(session: ApiTutorLiveSession | null | undefined, studentId: number | string): boolean {
    const added = closestNextOccurrence(session)?.added_users?.some(
        (user) => user?.id != null && String(user.id) === String(studentId)
    )
    return added === true && supportsNoShowRecovery(session?.live_room)
}

/**
 * Le drapeau de présence du jour (`NewStudentsListItem.js:271-295`) : « Impayé.e » d'abord (orange) ; sinon les
 * drapeaux du jour joints par une virgule, « Retard signalé {n} min » / « Absence signalée », en rouge dès
 * qu'il y a une absence. `today` : le jour local (l'heure arrive de l'appelant).
 */
export function attendanceFlagOf(
    session: ApiTutorLiveSession | null | undefined,
    student: ApiLiveStudent | null | undefined,
    today: moment.MomentInput
): UITutorLiveFlag | undefined {
    if (student?.is_payment_blocked === true) return { label: "Impayé.e", tone: "warning" }
    const flags = (session?.attendance_flags ?? []).filter(
        (flag): flag is ApiAttendanceFlag =>
            flag != null &&
            student?.id != null &&
            String(flag.user_id) === String(student.id) &&
            moment(flag.date).isSame(moment(today), "day")
    )
    const label = flags
        .map((flag) =>
            flag.type ? (flag.type === "late" ? `Retard signalé ${flag.late_minutes} min` : "Absence signalée") : ""
        )
        .join(", ")
    if (label === "") return undefined
    return { label, tone: flags.some((flag) => flag.type === "absent") ? "danger" : "warning" }
}

/**
 * Les faites de la liste (port de `getTodaysFinishedActivities`, `data/utility/dataUtils.js:534-549`) : celles
 * du jour, la plus récente d'abord, puis les deux dernières d'avant, cinq au plus.
 */
export function legacyDoneActivities(
    activities: (ApiLiveActivity | null)[] | null | undefined,
    today: moment.MomentInput
): ApiLiveActivity[] {
    const rows = (activities ?? []).filter((activity): activity is ApiLiveActivity => activity != null)
    const byRecent = (a: ApiLiveActivity, b: ApiLiveActivity) => moment(b.finished_at).diff(moment(a.finished_at))
    const finishedToday = rows
        .filter((activity) => moment(activity.finished_at).isSame(moment(today), "day"))
        .sort(byRecent)
    const lastBeforeToday = rows
        .filter((activity) => !moment(activity.finished_at).isSame(moment(today), "day"))
        .sort(byRecent)
        .slice(0, 2)
    return finishedToday.concat(lastBeforeToday).slice(0, 5)
}

/** Toutes ses faites, la plus récente d'abord : ce que « Voir plus » déplie. */
export function allDoneActivities(activities: (ApiLiveActivity | null)[] | null | undefined): ApiLiveActivity[] {
    return (activities ?? [])
        .filter((activity): activity is ApiLiveActivity => activity != null)
        .sort((a, b) => moment(b.finished_at).diff(moment(a.finished_at)))
}

/** Le statut de rendu d'une faite (l'ancien `DoneChip`) : validée, rejetée, sinon en attente. */
function reviewStatusOf(status: string | null | undefined): UITutorLiveActivity["reviewStatus"] {
    if (status === "validated") return "validated"
    if (status === "rejected") return "rejected"
    return "pending"
}

export type LiveActivityContext = {
    subjectsById: Record<string, ApiSchoolSubject>
    /** L'activité qu'il a devant lui (`useSeenStudentActivity`), s'il est là. */
    seenActivityId: string | null
    /** Les gabarits d'activité (`staticData.activity_templates`) : une photo ou un audio déposé. */
    templates?: ApiActivityTemplate[] | null
}

/** Un gabarit d'activité (`staticData.activity_templates`) : ses étapes disent photo ou audio. */
export type ApiActivityTemplate = { id?: number | string | null; steps?: ({ type?: string | null } | null)[] | null }

/** Photo ou audio, selon le gabarit (port de `getActivityType`, `RealizationsListItemContent.js:18-29`). */
export function mediaKindOf(
    raw: ApiLiveActivity,
    templates: ApiActivityTemplate[] | null | undefined
): "photo" | "audio" {
    const template = (templates ?? []).find(
        (candidate) => candidate?.id != null && String(candidate.id) === String(raw.activity_template_id)
    )
    return template?.steps?.some((step) => step?.type === "photo") ? "photo" : "audio"
}

/**
 * Une ligne de la liste (`RealizationsListItemContent.js`) : le titre (`material.title`, sinon `title`), la
 * matière (`school_subject_id`, sinon celle du support), le chapitre, le temps passé et prévu, les photos
 * déposées, et pour une faite sa date et son statut de rendu.
 */
export function toUITutorLiveActivity(
    raw: ApiLiveActivity,
    done: boolean,
    context: LiveActivityContext
): UITutorLiveActivity | null {
    if (raw.id == null) return null
    const id = String(raw.id)
    const subjectId = raw.school_subject_id ?? raw.material?.school_subject_id
    const subject = subjectId != null ? context.subjectsById[String(subjectId)] : undefined
    const onScreen = context.seenActivityId != null && context.seenActivityId === id
    const mediaCount = raw.media_count ?? 0
    const chapter = raw.student_chapter?.chapter?.name ?? undefined
    return {
        id,
        title: raw.material?.title ?? raw.title ?? "",
        // « en cours » : l'activité à faire qu'il a devant lui (le visage se pose dessus, #2931).
        status: done ? "done" : onScreen ? "current" : "todo",
        subject: subject != null ? toUISubject(subject) : undefined,
        onScreen: onScreen || undefined,
        minutes: spentMinutes(raw.duration) ?? undefined,
        estimatedMinutes: raw.estimated_duration ?? undefined,
        chapter: chapter ?? undefined,
        media:
            !done && raw.finished_at == null && mediaCount > 0
                ? { kind: mediaKindOf(raw, context.templates), count: mediaCount }
                : undefined,
        finishedLabel: done && raw.finished_at ? moment(raw.finished_at).format("DD/MM") : undefined,
        reviewStatus: done ? reviewStatusOf(raw.status) : undefined,
        part: done ? undefined : quizPartOf(raw),
    }
}

/**
 * La partie d'une activité à quiz où l'élève en est : le quiz tant qu'il ne l'a ni réussi ni passé, puis
 * l'exercice — la règle de la porte que l'élève franchit (`requiresPrerequisiteQuiz`). Sans quiz : rien.
 */
export function quizPartOf(raw: Pick<ApiLiveActivity, "has_prerequisite_quiz" | "quiz_status">) {
    if (raw.has_prerequisite_quiz !== true && raw.has_prerequisite_quiz !== 1) return undefined
    return raw.quiz_status == null || raw.quiz_status === "pending" ? "quiz" : "exercise"
}

/**
 * La liste de l'élève regardé : ses faites (la règle de l'ancienne liste, ou toutes une fois « Voir plus »
 * touché), puis ses activités à faire dans l'ordre du serveur (l'ordre que le glisser écrit).
 * `moreDone` : d'autres faites attendent derrière « Voir plus ».
 */
export function tutorLiveActivitiesOf(
    student: ApiLiveStudent,
    options: LiveActivityContext & { expanded: boolean; today: moment.MomentInput }
): { activities: UITutorLiveActivity[]; moreDone: boolean } {
    const all = allDoneActivities(student.activities)
    const shownDone = options.expanded ? all : legacyDoneActivities(student.activities, options.today)
    const done = shownDone
        .map((raw) => toUITutorLiveActivity(raw, true, options))
        .filter((row): row is UITutorLiveActivity => row != null)
    const todoRaw = (student.todo_activities ?? []).filter((raw): raw is ApiLiveActivity => raw != null)
    const nextId = nextLiveActivityId(todoRaw, options.seenActivityId)
    const todo = todoRaw
        .map((raw) => toUITutorLiveActivity(raw, false, options))
        .filter((row): row is UITutorLiveActivity => row != null)
        .map((row) => (row.id === nextId ? { ...row, next: true } : row))
    return { activities: [...done, ...todo], moreDone: !options.expanded && all.length > shownDone.length }
}

/**
 * L'activité que l'élève aura juste après s'il rend celle qu'il a devant lui (`currentId`) : celle que son écran
 * de fin lui ouvre (`useStudentActivityView.nextActivity` : `remainingTodo`, puis `getMostRecentActivity(…,
 * null)`), portée à la lettre. Parmi ses « à faire » sans celle-ci, la commencée le plus récemment ; à égalité ou
 * sans date (`started_at` vide vaut l'époque, comme `new Date(null)`), la première de la liste.
 */
export function nextLiveActivityId(todo: ApiLiveActivity[], currentId: string | null | undefined): string | null {
    const startedAt = (activity: ApiLiveActivity) => (activity.started_at ? Date.parse(activity.started_at) : 0)
    const next = todo
        .filter((activity) => activity.id != null && String(activity.id) !== currentId && !activity.finished_at)
        .reduce<ApiLiveActivity | null>(
            (latest, activity) => (latest == null || startedAt(activity) > startedAt(latest) ? activity : latest),
            null
        )
    return next?.id != null ? String(next.id) : null
}

/** Sa dernière conversation avec le champion : celle qu'un « interrompre » clôt (`LiveParticipant.js:152`). */
export function lastConversationId(student: ApiLiveStudent | null | undefined): number | string | null {
    const conversations = student?.conversations ?? []
    return conversations[conversations.length - 1]?.id ?? null
}

/** Le prénom affiché : `display_name` comme l'ancienne fiche, sinon le prénom. */
export function displayNameOf(student: ApiLiveStudent | null | undefined): string {
    return student?.display_name ?? student?.firstname ?? ""
}
